import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
  CtBinaryOperator,
  CtCardConfiguration,
  CtCardTemplate,
  CTDatatablePaginationParameter,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelService,
} from '@ctsolution/ct-framework';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';
import { CompanyRouteData } from '../../../_core/route-data/company.route-data';
import { WorkOrderStatusEnum } from '../../../_core/enum/work-order-state';

@Injectable()
export class DashboardCompanyCounterHelper {

  constructor(
    private router: Router,
    private managerService: CtManagerService,
    private modelService: CtModelService<any>,
  ) {}

  /**
   * Punto di ingresso: aggiunge le due card counter alla counterList
   * solo se il rispettivo override le ha rese visibili.
   */
  getCompanyCounterWidgets(counterList: CtCardConfiguration[]): void {

    this.getClientiAttiviCounter(counterList);
    this.getClientiPotenzialiCounter(counterList);

  }

  // ---------------------------------------------------------------------------
  // Card "Clienti" — anagrafiche con almeno un progetto attivo (commessa in
  // stato Active o InProgress) OPPURE almeno un'offerta confermata (commessa
  // con Quotation=false e stato Active/InProgress).
  // In pratica: Company che ha almeno un WorkOrder con Quotation=false
  // e State.Type IN [Active, InProgress].
  // ---------------------------------------------------------------------------
  private async getClientiAttiviCounter(counterList: CtCardConfiguration[]): Promise<void> {

    await this.managerService.getComponentOverride(
      'dashboard', 'companyActiveCounter', COMPANY_ACTIVE_COUNTER_CARD
    );

    if (!COMPANY_ACTIVE_COUNTER_CARD.visible) return;

    const result = await this.getCompanyCountByFilter([
      CTModelDatatableFilter.create()
  .setField('[WorkOrders].Exists() AND [WorkOrders][Quotation = false].Exists() = true')
  .setValue([])
  .setOperatorType(CtBinaryOperator.OrOperator),
    ]);

    const count = result?.TotalElement ?? 0;
    COMPANY_ACTIVE_COUNTER_CARD.setDescription(count > 0 ? String(count) : '0');

    const queryObject = {
      constants: [
        {
  Field: '[WorkOrders].Exists() AND [WorkOrders][Quotation = false].Exists() = true',
  OperatorType: 53,
  Value: [],
},
      ],
    };

    const urlTree: UrlTree = this.router.createUrlTree(
      ['/customer/business/list'],
      { queryParams: { q: JSON.stringify(queryObject) } }
    );

    COMPANY_ACTIVE_COUNTER_CARD.setAction(() =>
      window.open(this.router.serializeUrl(urlTree), '_blank')
    );

    counterList.push(COMPANY_ACTIVE_COUNTER_CARD);

  }

  // ---------------------------------------------------------------------------
  // Card "Clienti potenziali" — anagrafiche che hanno almeno un'offerta
  // (WorkOrder con Quotation=true) ma NESSUN progetto attivo
  // (nessun WorkOrder con Quotation=false).
  // ---------------------------------------------------------------------------
  private async getClientiPotenzialiCounter(counterList: CtCardConfiguration[]): Promise<void> {

    await this.managerService.getComponentOverride(
      'dashboard', 'companyPotentialCounter', COMPANY_POTENTIAL_COUNTER_CARD
    );

    if (!COMPANY_POTENTIAL_COUNTER_CARD.visible) return;

    const result = await this.getCompanyCountByFilter([
      CTModelDatatableFilter.create()
        .setField('[WorkOrders].Exists() AND [WorkOrders][Quotation = false].Exists() = false')
        .setValue([])
        .setOperatorType(CtBinaryOperator.OrOperator),
    ]);

    const count = result?.TotalElement ?? 0;
    COMPANY_POTENTIAL_COUNTER_CARD.setDescription(count > 0 ? String(count) : '0');

    const queryObject = {
      constants: [
        {
          Field: '[WorkOrders].Exists() AND [WorkOrders][Quotation = false].Exists() = false',
          OperatorType: 53,
          Value: [],
        },
      ],
    };

    const urlTree: UrlTree = this.router.createUrlTree(
      ['/customer/business/list'],
      { queryParams: { q: JSON.stringify(queryObject) } }
    );

    COMPANY_POTENTIAL_COUNTER_CARD.setAction(() =>
      window.open(this.router.serializeUrl(urlTree), '_blank')
    );

    counterList.push(COMPANY_POTENTIAL_COUNTER_CARD);

  }

  /**
   * Esegue getList sul controller `company` con i filtri forniti.
   * Restituisce il risultato grezzo da cui si legge TotalElement.
   */
  private getCompanyCountByFilter(
    filters: CTModelDatatableFilter[]
  ): Promise<CtModelConfiguration<any>> {

    const configuration = CtModelConfiguration
      .create()
      .setRouteData(CompanyRouteData());

    const operators = CtModelDatatableOperators
      .create()
      .setFilters(filters)
      .setPagination(new CTDatatablePaginationParameter().setElementsForPage(1));

    return new Promise<CtModelConfiguration<any>>(resolve => {
      this.modelService
        .getList(configuration, operators)
        .subscribe(response => resolve(response.Result));
    });

  }

}

// ---------------------------------------------------------------------------
// Card definitions — visible: false di default (backward-compatible).
// Il template usa CtCardTemplate.COUNTER per mostrare solo il numero.
// ---------------------------------------------------------------------------

export const COMPANY_ACTIVE_COUNTER_CARD: CtCardConfiguration = CtCardConfiguration.create()
  .setTitle('dashboard.companyActiveCounterLabel')
  .setVisible(false)
  .setTemplate(CtCardTemplate.COUNTER)
  .setClass('bg-ct-manager text-white');

export const COMPANY_POTENTIAL_COUNTER_CARD: CtCardConfiguration = CtCardConfiguration.create()
  .setTitle('dashboard.companyPotentialCounterLabel')
  .setVisible(false)
  .setTemplate(CtCardTemplate.COUNTER)
  .setClass('bg-ct-manager text-white');
